/**
 * @Description 标准版本-机会点详情-顶部
 */
import { FC, useEffect, useRef, useState, useMemo, useContext } from 'react';
import copy from 'copy-to-clipboard';
import { Badge, Button, Form, Modal, Space, message, Typography } from 'antd';
import { useSelector } from 'react-redux';
import styles from '../index.module.less';
import IconFont from '@/common/components/IconFont';
import { useMethods } from '@lhb/hook';

import {
  getChancePointDetail,
  importChancePoint,
  exportTemplateChancePoint,
  importChancePointStatus,
  importChancePointRecords,
  chancePointRecovery,
  postStandardShareFormCreate,
  postChancePointFollowRecordPage,
} from '@/common/api/expandStore/chancepoint';
import { createApproval, getApprovalDetail, revokeApproval } from '@/common/api/expandStore/approveworkbench';
import { V2Confirm } from '@/common/components/Others/V2Confirm';
import V2Operate from '@/common/components/Others/V2Operate';
import V2Title from '@/common/components/Feedback/V2Title';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
import V2Form from '@/common/components/Form/V2Form';
import V2FormTextArea from '@/common/components/Form/V2FormTextArea/V2FormTextArea';
import { deepCopy, downloadFile, isArray, isNotEmpty, isNotEmptyAny, refactorPermissions, replaceEmpty } from '@lhb/func';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import V2ImportModal from '@/common/components/business/V2ImportModal';
import ApprovalNodes from '@/common/components/business/ExpandStore/ApprovalNodes';
import ApprovalCreateDialog from '@/common/components/business/ExpandStore/ApprovalCreateDialog';
import ApproverDetailDrawer from '@/views/expandstore/pages/approver/components/DetailDrawer';
import { ChangePonitDetailType } from '../type';
import { ApprovalStatus, ApprovalType, ApprovalTypeValue, ChanceDetailPermission } from '@/common/components/business/ExpandStore/ts-config';
import AreaProtectionDrawer from '../../../AreaProtection/detail';
import { useBigdataFn } from '@/common/utils/bigdata';
import { ControlType, formDataComsToTiled, formDataEcho, getSubmitValuesByTiledFormData } from '../../../DynamicComponent/config';
import { JNPIdentification } from '../../../DynamicComponent/components/CalculationJNP/ts-config';
import { checkContractSign } from '@/common/api/expandStore/shop';
import ShopEditDrawer from '@/views/expandstore/pages/shopEdit/Drawer';
import ChanceDiscardModal, { ChanceDiscardData } from '@/common/components/business/ExpandStore/ChanceDiscardModal';
import UserInfoContext from '@/layout/context';
import Export from './Export';
import FollowRecordDrawer from './FollowRecordDrawer';
import CheckContractModal from './CheckContractModal';
import { ShopEditType } from '@/views/expandstore/pages/shopEdit/ts-config';
import { getAntiCollisionOperationState } from '../../../DynamicComponent/components/AreaProtection/collision';
import DecisionReportDrawer from '../../DecisionReportDrawer';
import MallDecisionReportDrawer from '@/common/components/business/ExpandStore/ChanceDetailDrawer/MallDecisionReport';

import { CHANCE_MODEL_SYNC_WARNING, prepareModelSyncApproval } from '../../modelSync';

/** 组件传递参数类型 */
interface TitleProps {
  detail: ChangePonitDetailType;
  setFormDrawerData;
  canEditForm: boolean; // 是否可编辑机会点详情表单
  onClose; // 关闭
  updateHandle; // 刷新详情
  updateModule; // 触发一鸣模块计算并保存
  saveHandle;
  onSearch?: Function; // 刷新外层列表
  onSearchCurrentPage?: Function; // 刷新外层列表当前页
  hintStr?: string;
  hideOperate? :boolean;
  isRecycle?: boolean;
  titleHeightRef: any;
  /** 是否存在 200m 内防撞店 */
  hasCollisionWithin200m?: boolean;
  /** 当前区域保护特殊申请状态；未配置区域保护时保持 undefined。 */
  antiCollisionApprovalStatus?: number;
  /** 特殊申请提交成功后重新获取机会点详情 */
  onDetailRefresh?: () => void;
}

const { Text } = Typography;

/** 机会点详情-顶部-标题/操作/审批 */
const Title: FC<TitleProps> = ({
  detail,
  setFormDrawerData,
  canEditForm,
  onClose,
  updateHandle,
  updateModule,
  saveHandle,
  onSearch,
  onSearchCurrentPage,
  hintStr,
  hideOperate = false,
  isRecycle = false,
  titleHeightRef,
  hasCollisionWithin200m,
  antiCollisionApprovalStatus,
  onDetailRefresh,
}) => {
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook
  const selfRef: any = useRef();
  const contractSignRef: any = useRef();
  const approvalPreparingRef = useRef(false);
  const approvalContextRef = useRef<object>({});
  const [preparingApproval, setPreparingApproval] = useState(false);

  const [disableCreateApproval, setDisableCreateApproval] = useState<boolean>(false); // 禁用提交审批按钮，提示过一次先区域保护后禁用
  const [showImportModal, setShowImportModal] = useState<boolean>(false); // 是否显示导入弹窗
  const templateAssets = [
    {
      // 数据占位符
      url: `${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/file/开店计划导入模板.xlsx`,
      name: '机会点模板.xlsx',
    },
  ];
  const [importModalWidth, setimportModalWidth] = useState<number>(340); // 导入组件宽度
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [approvalDetail, setApprovalDetail] = useState<any>({});
  const [approvalVisible, setApprovalVisible] = useState<boolean>(false);
  const [specialApplicationVisible, setSpecialApplicationVisible] = useState<boolean>(false);
  const [specialApplicationForm] = Form.useForm();
  const [approvalPropertyValues, setApprovalPropertyValues] = useState<any>([]);
  const [areaProtectOpen, setAreaProtectOpen] = useState<boolean>(false);
  const [discardData, setDiscardData] = useState<ChanceDiscardData>({});
  const [isLock, setIsLock] = useState<boolean>(false);
  const [contractApprovalData, setContractApprovalData] = useState<any>({
    id: null,
    open: false,
  });
  const { tenantInfo } = useContext(UserInfoContext); // 租户信息
  const [checkContractModalData, setCheckContractModalData] = useState<any>({
    open: false,
    data: null,
  });
  const [followRecordVisible, setFollowRecordVisible] = useState<boolean>(false);
  const [followRecordCount, setFollowRecordCount] = useState<number>(0);
  const [decisionReportVisible, setDecisionReportVisible] = useState<boolean>(false);
  const [saasDecisionVisible, setSaasDecisionVisible] = useState<boolean>(false);
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);
  useEffect(() => {
    approvalContextRef.current = {};
    approvalPreparingRef.current = false;
    setPreparingApproval(false);
    return () => { approvalContextRef.current = {}; };
  }, [detail?.id, canEditForm, hideOperate, tenantCheckData?.isYiMing]);
  // 是否为基诺浦模板
  const isJNP = useMemo(() => {
    if (!isNotEmptyAny(detail)) return false;

    const { moduleDetails } = detail;
    const module = isArray(moduleDetails) ? moduleDetails.find((itm: any) => itm.moduleType === 4) : null;
    if (!module || !isNotEmptyAny(module.infoModule)) return false;
    const { propertyGroupVOList } = module.infoModule || {};
    if (!isArray(propertyGroupVOList) || !propertyGroupVOList.length) return false;

    const allComs: any = [];
    formDataComsToTiled(propertyGroupVOList, allComs); // 将组件对象平铺到数组中
    const property = allComs?.find(itm => itm.identification === JNPIdentification);
    return isNotEmptyAny(property);
  }, [detail]);

  /**
   * @description 是否在提交审批前进行必须区域保护，且未提交区域保护
   * @return true
   */
  const isNoTaskModeWaitProtect = useMemo(() => {
    return !tenantInfo?.taskTemplateEnable && tenantInfo?.protectFlag && !detail?.protectInfo;
  }, [tenantInfo, detail]);

  /**
   * @description 是否在提交审批前进行必须区域保护，且区域保护还在申请中
   * @return true
   */
  const isNoTaskModeProtectApplying = useMemo(() => {
    return !tenantInfo?.taskTemplateEnable && tenantInfo?.protectFlag && detail?.protectInfo?.statusName && detail?.protectInfo?.statusName !== '保护中';
  }, [tenantInfo, detail]);

  const antiCollisionOperationState = useMemo(() => getAntiCollisionOperationState(
    hasCollisionWithin200m,
    antiCollisionApprovalStatus,
  ), [hasCollisionWithin200m, antiCollisionApprovalStatus]);

  const methods = useMethods({
    /* 点击 导入审批表 */
    async handleImport() {
      // 判断有没有上传历史
      await methods.getHistoy();
      setShowImportModal(true);
    },

    /*
      点击 编辑
      可在详情页内直接编辑表单时，保存表单；页面内不可编辑时，去编辑页
    */
    handleEdit() {
      bigdataFn('4482ac55-09de-4a43-9aee-30d86eac741c', '机会点详情-保存', '', { op_block: '机会点管理', op_event: ``, });
      if (canEditForm) {
        // saveHandle入参分别为needCheck，成功回调
        // 最终方法saveHandle在 src/common/components/business/ExpandStore/ChanceDetailDrawer/Modules/ChanceForm/index.tsx
        saveHandle(false, () => {
          V2Message.success('保存成功');
          onClose();
        });
        return;
      }
      setFormDrawerData({
        id: detail.id,
        open: true,
        templateId: '',
        antiCollisionApprovalStatus,
      });
    },

    /** 触发一鸣指定字段的关联计算并保存。 */
    handleUpdateModule() {
      if (!tenantCheckData?.isYiMing) return;
      updateModule();
    },

    /** 点击发起审批 */
    async handleSubmitApproval() {
      bigdataFn('38047dca-f7bd-4f05-bced-6d5eed4eac34', '机会点详情-提交审批', '', { op_block: '机会点管理', op_event: ``, });
      if (isNoTaskModeWaitProtect || isNoTaskModeProtectApplying) {
        if (!disableCreateApproval) { // 提示一次后禁用
          V2Message.warning(isNoTaskModeProtectApplying ? '请等待区域保护申请通过' : '请先提交区域保护');
          setDisableCreateApproval(true);
        }
        return;
      }
      if (approvalPreparingRef.current) return;
      const enabled = !!tenantCheckData?.isYiMing && canEditForm && !hideOperate &&
        operateList.some(btn => btn.event === 'updateModule' && !btn.hide && !btn.disabled);
      if (enabled) {
        approvalPreparingRef.current = true;
        setPreparingApproval(true);
        const context = approvalContextRef.current;
        const canContinue = await prepareModelSyncApproval({
          enabled,
          id: detail.id,
          message: CHANCE_MODEL_SYNC_WARNING,
          getDetail: getChancePointDetail,
          synchronize: latestDetail => new Promise<boolean>(resolve => {
            updateModule(resolve, { latestDetail, shouldRefreshAfterSave: false, showModelSyncSuccess: false });
          }),
          isCurrent: () => approvalContextRef.current === context,
        });
        if (approvalContextRef.current !== context) return;
        approvalPreparingRef.current = false;
        setPreparingApproval(false);
        if (!canContinue) return;
      }
      methods.continueSubmitApproval();
    },

    /** 前置同步结束后，保持原完整校验、预保存和审批弹窗流程。 */
    continueSubmitApproval() {
      if (canEditForm) {
        // saveHandle入参分别为needCheck，成功回调
        // 最终方法saveHandle在 src/common/components/business/ExpandStore/ChanceDetailDrawer/Modules/ChanceForm/index.tsx
        saveHandle(true, (propertyValues: any) => {
          // 机会点保存成功后，执行发起处理
          setApprovalPropertyValues(propertyValues || []);
          setApprovalVisible(true);
        }, false); // 理由弹窗确认前仅预保存，取消时不能刷新详情而改变当前按钮状态。
      } else {
        setApprovalVisible(true);
      }
    },

    openSpecialApplication() {
      setSpecialApplicationVisible(true);
    },
    closeSpecialApplication() {
      setSpecialApplicationVisible(false);
      specialApplicationForm.resetFields();
    },
    createSpecialApplication(remark: string) {
      createApproval({
        id: detail.id,
        type: ApprovalType.AreaProtection,
        typeValue: ApprovalTypeValue.AreaProtection,
        remark,
      }).then((res) => {
        if (res) {
          V2Message.success('发起特殊申请成功');
          methods.closeSpecialApplication();
          onDetailRefresh?.();
        }
      });
    },
    submitSpecialApplication() {
      specialApplicationForm.validateFields().then((values) => {
        if (canEditForm) {
          // 与提交审批保持一致：完整校验并保存成功后，再发起特殊申请。
          saveHandle(true, () => methods.createSpecialApplication(values.remark));
          return;
        }
        methods.createSpecialApplication(values.remark);
      });
    },

    /** 点击撤销审批 */
    async handleRevokeApproval() {
      // 一个机会点可能会被多个审批节点绑定，但是只有一项在审批中，撤销审批只撤销在【审批中 item.approvalStatus === 0】的审批
      const approvalingItem = detail?.approvalInfos.find(element => element.approvalStatus === 0); // 正在审批中的元素

      if (approvalingItem) {
        const { approvalId, approvalType, approvalTypeValue } = approvalingItem || {};
        revokeApproval({
          id: approvalId,
          relationType: approvalType,
          typeValue: approvalTypeValue,
        }).then(() => {
          V2Message.success('撤销审批成功');
          onSearch?.();
          onClose();
        }).catch(() => {
          V2Message.success('撤销审批失败，请稍后再试');
        });
      }
    },

    // 门店签约
    handleContractApply: async() => {
      let isPass = false;
      try {
        const params = {
          chancePointId: detail.id,
        };
        const res = await checkContractSign(params);
        const { allow, errorCodeList } = res || {};
        isPass = allow;
        !allow && isArray(errorCodeList) && errorCodeList.length && setCheckContractModalData({
          open: true,
          data: errorCodeList
        });
      } catch (error) {}
      if (!isPass) {
        // 不允许继续流程了
        return;
      }
      const { approvalId, approvalStatus } = detail?.contractApprovingInfo || {};
      if (approvalId && approvalStatus === ApprovalStatus.Untreated) {
        // 签约审批中
        setContractApprovalData({
          id: approvalId,
          open: true,
        });
      } else {
        // 还未生成审批，则允许发起审批，或直接签约通过
        contractSignRef.current.init({
          id: detail.id,
          type: ShopEditType.Sign,
        });
      }
    },

    // 废弃
    handleRecycle() {
      setDiscardData({
        id: detail.id,
        name: detail.name,
        open: true,
      });
    },

    // 恢复
    handleRecovery() {
      V2Confirm({
        content: '是否确认重新启用',
        onOk: () => {
          this.confirmRecovery();
        },
      });
    },
    confirmRecovery() {
      if (isLock) return;
      setIsLock(true);
      chancePointRecovery({ chancePointId: detail.id }).then(() => {
        V2Message.success('操作成功');
        onSearchCurrentPage?.(); // 为了请求后继续浏览当前页
        onClose();
      }).finally(() => {
        setIsLock(false);
      });
    },

    // 获取模板
    async getTemplateAssets() {
      const { url } = await exportTemplateChancePoint({ id: detail?.id });
      if (!url) return;
      // url && setTemplateAssets([{
      //   url,
      //   name: '机会点模板.xlsx'
      // }]);
      downloadFile({
        url,
        name: '机会点模板.xlsx',
      });
    },

    // 自定义逻辑
    async customUploadFetch(values, callbackFn) {
      const target = values?.url?.[0];
      if (!target) return;
      const { url, name } = target;
      const urlStr = `${url}?attname=${name}`;
      const params = { url: urlStr, excelName: name, chancePointId: detail.id };
      // importCheck(param);
      try {
        const result = await importChancePointStatus(params);
        if (result?.status === 1) {
          message.warn('当前导入的数据和上一版数据完全一致，请确认导入数据是否正确');
          callbackFn({ isError: true });
        } else {
          V2Confirm({
            onSure: () => {
              methods.importFile(params, callbackFn);
            },
            content: '当前导入的数据和上一版数据不一致，请确认导入数据是否正确',
            zIndex: 1003,
          });
        }
      } catch (e) {
        callbackFn({ isError: true });
      }
    },

    /** 上传文件 */
    importFile(params: any, callbackFn) {
      importChancePoint(params).then(
        () => {
          message.success('导入完成');
          callbackFn && callbackFn();
          updateHandle();
        },
        () => {
          callbackFn && callbackFn({ isError: true });
        }
      );
    },

    /** 获取导入历史 */
    getHistoy() {
      importChancePointRecords({ id: detail.id }).then((data: any) => {
        if (isArray(data) && data.length) {
          setHistoryData(data.splice(0, 5)); // 展示最近的5条
          setimportModalWidth(720);
          return;
        }
        setHistoryData([]);
      });
    },

    /** 获取审批详情 */
    getApprovalDetailData(target: any) {
      const { approvalId } = target;
      getApprovalDetail({ id: approvalId }).then((data) => {
        if (data && isNotEmpty(data.status) && isNotEmpty(data.statusName)) { // 审批撤销的情况下status与statusName为空，撤销情况下不需要展示审批信息
          setApprovalDetail(data);
        } else {
          setApprovalDetail({});
        }
      }).finally(() => {
        methods.getHeight();
      });
    },

    /**
     * 获取高度
     */
    getHeight: () => {
      setTimeout(() => {
        titleHeightRef.current = selfRef.current?.getBoundingClientRect()?.height || 0;
      }, 0);
    },
    /**
     * 点击区域保护按钮
     */
    handleSubmitProtect() {
      setAreaProtectOpen(true);
      bigdataFn('a2a3c49c-527f-4765-aefa-cfe2beb8dce9', '机会点详情-区域保护', '点击了机会点详情-区域保护', { op_block: '', op_event: ``, });
    },
    /** 打开跟进记录抽屉。 */
    openFollowRecordDrawer() {
      setFollowRecordVisible(true);
    },
    /** 关闭跟进记录抽屉。 */
    closeFollowRecordDrawer() {
      setFollowRecordVisible(false);
    },
    handleYmDecision() {
      setDecisionReportVisible(true);
    },
    closeDecisionReport() {
      setDecisionReportVisible(false);
    },
    handleSaasDecision() {
      setSaasDecisionVisible(true);
    },
    closeSaasDecision() {
      setSaasDecisionVisible(false);
    },
    /** 获取跟进记录总数，仅在按钮权限初始化时调用一次。 */
    getFollowRecordCount() {
      postChancePointFollowRecordPage({
        chancePointId: detail.id,
        page: 1,
        size: 1,
      }).then((data) => {
        setFollowRecordCount(Number(data?.totalNum) || 0);
      });
    },
    /** 新增记录后使用抽屉列表接口返回的总数同步右上角数量。 */
    updateFollowRecordCount(totalNum: number) {
      setFollowRecordCount(totalNum);
    },
    /**
     * @description 对权限数组设置主按钮（type属性，主按钮'primary'，其他为'default'）
     * @param perms 权限数组
     * @return 处理后的权限数组
     */
    dealHighlightButton(perms: any[]) {
      /*
        设置主功能按钮，
        有编辑按钮，编辑按钮作为主按钮
        无编辑按钮，默认将最后一个按钮蓝底高亮。遇到不可作为主功能的例外功能（如废弃）则跳过将倒数第二个设置为高亮
      */
      // 判断是否有编辑按钮
      const hasEditPerm = !!perms.find((item: any) => item.event === ChanceDetailPermission.Edit);
      if (hasEditPerm) {
        // 有编辑按钮
        const res = perms.map((item) => ({
          ...item,
          // 编辑按钮 蓝色
          type: item.event === ChanceDetailPermission.Edit ? 'primary' : 'default',
        }));
        return res;
      }

      const len = perms.length;
      const notMainEvents = [ChanceDetailPermission.Recycle]; // 不显示为主功能的按钮事件
      let hasSetMain = false; // 是否已设置过主功能按钮
      const res: any = [];
      for (let i = len - 1; i >= 0; i--) {
        const item = perms[i];
        if (!hasSetMain && !notMainEvents.includes(item.event)) {
        // 找到了要设置为主功能的按钮
          item.type = 'primary';
          hasSetMain = true;
        } else {
          item.type = 'default';
        }
        res.push(item);
      }
      res.reverse();
      return res;
    },
    /**
     * @description 对权限按钮排序，主按钮排到最后
     * @param perms 权限数组
     * @return 处理后的权限数组
     */
    sortHighlightButton(perms: any[]) {
      const defaultBtns = perms.filter(item => item.type !== 'primary');
      const primaryBtns = perms.filter(item => item.type === 'primary');
      const res = [...defaultBtns, ...primaryBtns];
      return res;
    },

    /** 邀请好友填写 */
    handleShareEdit() {
      const moduleDetails = deepCopy(Array.isArray(detail?.moduleDetails) ? detail?.moduleDetails : []);
      const module: any = moduleDetails.find((itm: any) => itm.moduleType === 4);
      const propertyGroupVOList = module?.infoModule?.propertyGroupVOList || [];
      const tiledFormData = [];
      // console.log('propertyGroupVOList', JSON.parse(JSON.stringify(propertyGroupVOList)));
      // 参考 src/common/components/business/ExpandStore/ChanceDetailDrawer/Modules/ChanceForm/index.tsx 的 onFormDetail
      formDataComsToTiled(propertyGroupVOList, tiledFormData); // 将组件对象平铺到数组中
      Array.isArray(tiledFormData) && tiledFormData.forEach((item: any) => {
        if (item.controlType === ControlType.SUB_FORM.value) { // 主要为了特殊处理子表单数据结构，其他控件不用处理
          item.textValue = JSON.stringify(formDataEcho(item));
        }
      });
      // console.log('tiledFormData', JSON.parse(JSON.stringify(tiledFormData)));
      const propertyValues = getSubmitValuesByTiledFormData(tiledFormData);

      postStandardShareFormCreate({
        /* 属性值 [{ "id": 458787, "propertyId": 428, "identification": "basicChancePointName", "controlType": 3, "textValue": "测试2287886" }] */
        propertyValues,
        relationId: detail.id, // 机会点 id
        relationType: 1, // 1 表示机会点
      }).then(res => {
        if (res?.id) {
          const url = `【请完善信息】${replaceEmpty(detail.name)}：${process.env.STORE_ASSISTANT_URL}/standardstore/chanceimprove?id=${res?.id}&share=1`;
          copy(url);
          V2Message.success('链接已复制，邀请好友填写信息');
        }
      });
    },

  });

  /**
   * @description 按钮权限列表
   * @return
   *   特殊处理
   *     a.如果可以编辑表单，将edit权限替换为保存按钮
   *     b.针对提交审批按钮，租户未开启任务模板+已开启区域保护+机会点未完成区域保护申请+已经点击提交审批并taost提示过，按钮禁用置灰
   */
  const operateList = useMemo(() => {
    if (!Array.isArray(detail?.permissions)) return [];

    let refactorPerms = refactorPermissions(detail.permissions);

    // 如果支持页面内编辑，将编辑按钮标题改为保存
    if (canEditForm) {
      refactorPerms = refactorPerms.map((itm: any) => itm.event === ChanceDetailPermission.Edit ? { ...itm, name: '保存' } : itm);
    }
    // 提交审批按钮有些情况下需要设置为禁用
    refactorPerms = refactorPerms.map((itm: any) => itm.event === ChanceDetailPermission.SubmitApprove ? { ...itm, disabled: disableCreateApproval } : itm);
    // 防撞店特殊申请审核中时，详情中不允许再进入任何编辑入口。
    if (antiCollisionApprovalStatus === 0) {
      refactorPerms = refactorPerms.filter((itm: any) => itm.event !== ChanceDetailPermission.Edit);
    }
    // 200m 内撞店时，提交审批入口由特殊申请审批状态控制。
    if (antiCollisionOperationState.hideSubmitApproval) {
      refactorPerms = refactorPerms.filter((itm: any) => itm.event !== ChanceDetailPermission.SubmitApprove);
    }
    // 过滤掉导出报告与跟进记录按钮，另外单独展示；模型同步仅在编辑态展示
    refactorPerms = refactorPerms.filter((itm: any) => (
      itm.event !== ChanceDetailPermission.ReportExport &&
      itm.event !== ChanceDetailPermission.Record &&
      // PC 详情不展示关联点位库，保留机会点列表中的关联入口。
      itm.event !== 'relationPlaceSpot' &&
      (itm.event !== 'updateModule' || canEditForm)
    ));

    // 设置主按钮
    let res = methods.dealHighlightButton(refactorPerms);
    // 将主按钮移到最后
    res = methods.sortHighlightButton(res);
    return preparingApproval ? res.map(btn => ({ ...btn, disabled: true })) : res;
  }, [detail, canEditForm, disableCreateApproval, antiCollisionApprovalStatus, antiCollisionOperationState.hideSubmitApproval, preparingApproval]);

  /** 是否具有查看跟进记录权限。 */
  const hasFollowRecordPermission = useMemo(() => {
    if (!isArray(detail?.permissions)) return false;

    const refactorPerms = refactorPermissions(detail.permissions);
    return !!refactorPerms.find((itm: any) => itm.event === ChanceDetailPermission.Record);
  }, [detail?.permissions]);

  useEffect(() => {
    if (!hasFollowRecordPermission || !detail?.id) return;

    methods.getFollowRecordCount();
  }, [detail?.id, hasFollowRecordPermission]);

  // 编辑/导入权限
  useEffect(() => {
    // 判断是否有审批
    const { approvalInfos } = detail || {};
    if (isArray(approvalInfos) && approvalInfos.length) {
      const target = approvalInfos[0]; // 目前只有一种类型只需要取第一个就行
      target && methods.getApprovalDetailData(target);
    } else {
      setApprovalDetail({}); // 重置审批信息
    }
    methods.getHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  return detail ? (
    <div ref={selfRef} className={styles.detailHeader}>
      {/* 点位名称以及操作按钮，废弃机会点只展示重新启用按钮 */}
      <V2Title
        className={styles.detailHeaderTitle}
        extra={
          (
            isRecycle ? <V2Operate
              operateList={operateList}
              onClick={(btn) => methods[btn.func]()}
              showBtnCount={5}
            /> : !hideOperate ? <div>
              <Space>
                {/* 跟进记录固定展示在报告导出左侧。 */}
                {hasFollowRecordPermission && <Badge
                  count={followRecordCount}
                  overflowCount={99}
                  offset={[followRecordCount > 99 ? -12 : -8, 0]}
                >
                  <Button onClick={methods.openFollowRecordDrawer}>跟进记录</Button>
                </Badge>}
                {/* 顺序对调，将导出报告按钮放在前面 */}
                <Export
                  detail={detail}
                  approvalDetail={approvalDetail}
                />
                <V2Operate
                  operateList={operateList}
                  onClick={(btn) => methods[btn.func]()}
                  showBtnCount={5}
                />
                {antiCollisionOperationState.showSpecialApplication && <Button type='primary' danger onClick={methods.openSpecialApplication}>发起特殊申请</Button>}
              </Space>
            </div> : <></>)
        }
      >
        <Text
          style={ { width: '100%' }}
          ellipsis={{ tooltip: detail.name }}
          className={styles.spotName}
        >
          {replaceEmpty(detail.name)}
        </Text>
      </V2Title>
      <div className={styles.detailHeaderBottom}>
        {!!isJNP && <Text
          style={ { width: '100%' }}
          ellipsis={{ tooltip: detail.address }}
          className={styles.spotAddress}
        >
          <IconFont iconHref='iconIC_dizhi' className={styles.spotAddressIcon}></IconFont>
          {replaceEmpty(detail.address)}
        </Text>}
        {isNotEmpty(hintStr) && <div className='c-f23'>{hintStr}</div>}
        {/* 当含有审批状态的时候渲染 */}
        {isNotEmptyAny(approvalDetail) ? <ApprovalNodes detail={approvalDetail} /> : null}
      </div>

      <V2ImportModal
        isOpen={showImportModal}
        closeModal={() => setShowImportModal(false)}
        title='导入机会点'
        modalWidth={importModalWidth}
        templateAssets={templateAssets}
        firstStepTemplateContent={
          <div className={styles.item}>
            <V2DetailItem
              type='files'
              filePreviewHide
              fileDownloadHide
              assets={templateAssets}
              rightSlot={{
                icon: <IconFont iconHref='icondownload' className='c-006' />,
                onIconClick: () => methods.getTemplateAssets(),
              }}
            />
          </div>
        }
        rightSlot={
          historyData?.length > 0 ? (
            <>
              {historyData.map((item: any, index: number) => (
                <V2DetailItem
                  key={index + item.name}
                  type='files'
                  assets={[
                    {
                      url: item.url,
                      name: item.name,
                    },
                  ]}
                />
              ))}
            </>
          ) : null
        }
        customUploadFetch={(values, callbackFn) => methods.customUploadFetch(values, callbackFn)}
      />
      <ApprovalCreateDialog
        visible={approvalVisible}
        setVisible={setApprovalVisible}
        id={detail.id}
        type={ApprovalType.ChancePoint}
        typeValue={ApprovalTypeValue.ChancePointEvaluate}
        specialParams={{
          propertyValues: approvalPropertyValues
        }}
        onComplete={() => {
          onSearch?.();
          onClose();
        }}/>
      <Modal
        title='发起特殊申请'
        open={specialApplicationVisible}
        destroyOnClose
        cancelText='取消'
        okText='提交申请'
        onCancel={methods.closeSpecialApplication}
        onOk={methods.submitSpecialApplication}
      >
        <V2Form form={specialApplicationForm}>
          <V2FormTextArea
            label='突破区域保护(撞店特批)-申请原因'
            name='remark'
            placeholder='请输入申请原因，最多输入500字'
            rules={[{ required: true, message: '请输入特殊申请原因' }]}
            config={{ showCount: true, maxLength: 500, autoSize: { minRows: 2, maxRows: 2 } }}
          />
        </V2Form>
      </Modal>
      {/* 区域保护弹窗 */}
      <AreaProtectionDrawer
        id={detail.id}
        open={areaProtectOpen}
        setOpen={setAreaProtectOpen}
        updateHandle={() => {
          setDisableCreateApproval(false); // 更新的时候恢复按钮状态
          updateHandle();
        }}
      />
      {/* 合同签约 */}
      <ShopEditDrawer ref={contractSignRef} reload={() => {
        updateHandle();
        onSearch?.();
      }}/>
      {/* 废弃操作弹框 */}
      <ChanceDiscardModal
        modalData={discardData}
        setModalData={setDiscardData}
        onSuccess={() => {
          onSearchCurrentPage?.(); // 为了请求后继续浏览当前页
          onClose();
        }}
      />
      {/* 审批详情抽屉 */}
      <ApproverDetailDrawer
        id={contractApprovalData?.id}
        open={contractApprovalData?.open} // 是否可见
        setOpen={(op) => {
          setContractApprovalData((state) => ({
            ...state,
            open: op,
          }));
        }}
        onRefresh={() => onSearch?.()} // 刷新列表
      />
      <CheckContractModal
        modalData={checkContractModalData}
        close={() => setCheckContractModalData({
          open: false,
          data: null
        })}
        handleAreaProtection={() => {
          setCheckContractModalData({
            open: false,
            data: null
          });
          setAreaProtectOpen(true);
        }}
      />
      {/* 跟进记录 */}
      <FollowRecordDrawer
        chancePointId={detail.id}
        open={followRecordVisible}
        onClose={methods.closeFollowRecordDrawer}
        onRecordAdded={methods.updateFollowRecordCount}
      />
      <DecisionReportDrawer
        detail={detail}
        open={decisionReportVisible}
        onClose={methods.closeDecisionReport}
      />
      <MallDecisionReportDrawer
        chancePointId={detail.id}
        open={saasDecisionVisible}
        onClose={methods.closeSaasDecision}
      />
    </div>
  ) : null;
};

export default Title;
